import { useState } from "react";
import { useSelector } from "react-redux";
import { Grid } from "antd";
import useRestoreFlightTimer from "../../hooks/useRestoreFlightTimer";

import { JourneyDynamicDetailsProvider, JourneyStaticDetailsProvider, StepButtonsPortalProvider } from "./context";
import { PassengerInputType, INITIAL_STEPS_VALIDATION, ValidityState } from "../../constants";
import type { JourneyDetailsLoaderData } from "./JourneyDetails.loader";

import { useGetFlightDetailsQuery } from "src/store/slices/flights/Flights";
import { generateInitialPassengerValues } from "./Steps/Passengers/utils";
import FareQuoteError from "./components/FareQuoteError/FareQuoteError";
import { withConditionalComponent } from "src/HOC/WithMobileComponent";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import JourneyDetailsWeb from "./layouts/JounrneyDetails.web";
import JourneyDetailsMob from "./layouts/JourneyDetails.mob";
import { RootState } from "src/store";
import { computeTotalFare } from "./hooks/useTotalFare";
import usePayment from "./Steps/Payment/hooks/usePayment";

const JourneyDetailsLayout = withConditionalComponent(JourneyDetailsWeb, JourneyDetailsMob);

export default function JourneyDetails({ journeySearchData, searchObject }: JourneyDetailsLoaderData) {
  useRestoreFlightTimer(journeySearchData._session);
  const journeyDetails = useGetFlightDetailsQuery(journeySearchData.journeyId);
  const { error } = journeyDetails;
  const passengersState = useState<PassengerInputType>(() => {
    return generateInitialPassengerValues(searchObject, journeySearchData);
  });
  const validityState = useState<ValidityState>(INITIAL_STEPS_VALIDATION);
  const isLargeScreen = !!Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];

  const [passengers] = passengersState;
  const userDetails = useSelector((state: RootState) => state.client.userDetails);
  const { totalPrice, pointsFactor, isFetching } = computeTotalFare(journeyDetails.data, passengers, userDetails.redeemFactor, journeyDetails.isFetching);

  const payment = usePayment({
    conversionFactor: pointsFactor,
    priceInCash: totalPrice,
    priceInPoints: totalPrice * pointsFactor,
    userTotalPoints: userDetails.totalPointsBalance,
    isFetching,
  });

  return (
    <StepButtonsPortalProvider>
      <JourneyStaticDetailsProvider value={{ journeyDetails, searchObject, journeySearchData }}>
        <JourneyDynamicDetailsProvider value={{ passengersState, validityState, payment }}>
          <JourneyDetailsLayout trigger={isLargeScreen} />
          {error && <FareQuoteError />}
        </JourneyDynamicDetailsProvider>
      </JourneyStaticDetailsProvider>
    </StepButtonsPortalProvider>
  );
}
